import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HeartIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import { useAddToWishlist, useRemoveFromWishlist, type ProductListItem } from '../../hooks/useProducts';
import { useCurrency } from '../../hooks/useCurrency';
import { useAuthStore } from '../../stores/authStore';

interface ProductCardProps {
  product: ProductListItem;
  isInWishlist?: boolean;
}

export default function ProductCard({ product, isInWishlist = false }: ProductCardProps) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const addToWishlist = useAddToWishlist();
  const removeFromWishlist = useRemoveFromWishlist();
  const { convert } = useCurrency();
  const isRTL = i18n.dir() === 'rtl';

  const handleWishlistClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (isInWishlist) {
      removeFromWishlist.mutate(product.id);
    } else {
      addToWishlist.mutate(product.id);
    }
  };


  return (
    <Link
      to={`/product/${product.slug}`}
      className="block bg-white rounded-lg overflow-hidden hover:shadow-md transition-shadow group"
    >
      {/* Image Container */}
      <div className="relative aspect-square overflow-hidden bg-gray-50">
        <img
          src={product.images[0] || '/placeholder-product.jpg'}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />

        {/* Wishlist Button */}
        <button
          onClick={handleWishlistClick}
          className={`absolute top-2 end-2 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
            isInWishlist
              ? 'bg-primary text-white'
              : 'bg-white/90 text-gray-600 hover:text-primary opacity-0 group-hover:opacity-100'
          }`}
          style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
        >
          {isInWishlist ? (
            <HeartSolidIcon className="w-4 h-4" />
          ) : (
            <HeartIcon className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* Content */}
      <div className="p-3">
        {/* Brand Badge */}
        {product.brand && (
          <span className="inline-block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1">
            {product.brand.replace(/_/g, ' ')}
          </span>
        )}

        {/* Product Name */}
        <h3 className="text-sm text-gray-800 line-clamp-2 min-h-[2.5rem] mb-2 group-hover:text-primary transition-colors">
          {product.name}
        </h3>

        {/* Rating */}
        {product.rating !== undefined && product.rating > 0 && (
          <div className="flex items-center gap-1 mb-2">
            <div className="flex">
              {[1, 2, 3, 4, 5].map((star) => (
                <svg
                  key={star}
                  className={`w-3 h-3 ${
                    star <= Math.round(product.rating!)
                      ? 'text-amber-400'
                      : 'text-gray-200'
                  }`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
            </div>
            <span className="text-xs text-gray-400">({Math.floor(Math.random() * 500) + 50})</span>
          </div>
        )}

        {/* Price Section */}
        <div className="flex items-baseline gap-2">
          <span className="text-lg font-bold text-primary">
            ₪{convert(product.price).toFixed(0)}
          </span>
        </div>

        {/* Free Shipping */}
        <div className="flex items-center gap-1 mt-2 text-xs text-green-600">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span>{isRTL ? 'משלוח חינם' : 'Free Shipping'}</span>
        </div>
      </div>
    </Link>
  );
}
